/**
 * Mock data for the Trust module.
 *
 * Covers 7 compliance frameworks + universal primitives:
 * - Frameworks: ITAR, CMMC L2, HIPAA, GDPR, SOC 2 Type II, ISO 27001:2022, ISO 42001
 * - Plus FINOS AI Governance v2 as a built-in policy pack
 *
 * Universal primitives (many-to-many with frameworks):
 * - Controls (with cross-framework mappings)
 * - Evidence Vault
 * - Risk Register
 * - AI Systems Inventory
 * - Vendor Registry
 * - Breach Register
 * - DSAR Queue
 * - Verifications (US Persons, Clearances, Training)
 * - Policies
 * - Auditor Portal sessions
 *
 * Architecture: One piece of evidence (e.g., "AES-256 at rest") maps to many
 * framework controls (HIPAA 164.312, CMMC SC.L2-3.13.11, ISO A.8.24,
 * SOC 2 CC6.1, GDPR Art 32). This is how Vanta/Drata/Secureframe scale to
 * 30+ frameworks without exponential effort.
 */

// ─── Framework Definitions ──────────────────────────────────────────────────

export type FrameworkId =
  | 'itar'
  | 'cmmc_l2'
  | 'hipaa'
  | 'gdpr'
  | 'soc2'
  | 'iso27001'
  | 'iso42001'
  | 'finos_ai_v2'
  | 'nist_ai_rmf'
  | 'fedramp_moderate'
  | 'pci_dss';

export type FrameworkStatus =
  | 'not_started'
  | 'setup_in_progress'
  | 'in_progress'
  | 'audit_ready'
  | 'certified'
  | 'recertification_due';

export type FrameworkCategory =
  | 'security'
  | 'privacy'
  | 'export_control'
  | 'healthcare'
  | 'ai_governance';

export interface Framework {
  id: FrameworkId;
  name: string;
  shortName: string;
  fullName: string;
  description: string;
  category: FrameworkCategory;
  authority: string;
  jurisdiction: string;
  controlCount: number;
  status: FrameworkStatus;
  /** Sysadmin-licensed: whether the tenant's contract tier includes this framework. If false, cannot be activated. */
  licensed: boolean;
  /** Tenant-activated: whether this framework is actively being tracked. Drives cross-module filtering. */
  enabled: boolean;
  /** When the framework was activated by the tenant admin. */
  activatedAt: string | null;
  /** Who activated the framework (tenant admin). */
  activatedByUserId: string | null;
  /** Whether setup wizard was completed at activation. */
  setupCompleted: boolean;
  /** Setup answers keyed by question ID (populated by activation wizard). */
  setupAnswers: Record<string, string | string[] | boolean>;
  setupProgress: number;        // 0-100 % of setup wizard complete
  controlsImplemented: number;
  controlsPassing: number;
  controlsFailing: number;
  controlsNotApplicable: number;
  evidenceCount: number;
  evidenceFresh: number;          // not expired
  evidenceStale: number;          // expired or expiring in 30d
  lastAssessmentDate: string | null;
  nextAssessmentDate: string | null;
  certificationExpiry: string | null;
  assessor: string | null;        // C3PAO / CPA firm / DPA
  ownerUserId: string | null;
  retentionYears: number;
  updateFrequency: 'continuous' | 'monthly' | 'quarterly' | 'annual';
  enforcementDate: string | null;
  iconColor: string;
  recentChanges: number;
  openFindings: number;
  riskRating: 'low' | 'medium' | 'high' | 'critical';
}

// ─── Controls (Universal + Per-Framework Mapping) ───────────────────────────

export type ControlStatus = 'passing' | 'failing' | 'partial' | 'needs_review' | 'not_applicable' | 'not_implemented';
export type ControlAutomation = 'automated' | 'manual' | 'hybrid';
export type ControlPriority = 'critical' | 'high' | 'medium' | 'low';
export type ControlFamily =
  | 'access_control'
  | 'identification_authentication'
  | 'audit_accountability'
  | 'configuration_management'
  | 'incident_response'
  | 'maintenance'
  | 'media_protection'
  | 'personnel_security'
  | 'physical_protection'
  | 'risk_assessment'
  | 'security_assessment'
  | 'system_communications'
  | 'system_information_integrity'
  | 'awareness_training'
  | 'data_governance'
  | 'ai_governance'
  | 'privacy_controls'
  | 'export_controls'
  | 'incident_management'
  | 'business_continuity'
  | 'vendor_management';

export interface ControlMapping {
  framework: FrameworkId;
  controlId: string;             // e.g., "HIPAA:164.312(a)(2)(iv)"
  controlName: string;
  relevance: 'primary' | 'supporting';
}

export interface Control {
  id: string;
  name: string;
  description: string;
  family: ControlFamily;
  status: ControlStatus;
  automation: ControlAutomation;
  priority: ControlPriority;
  ownerUserId: string;
  ownerName: string;
  mappings: ControlMapping[];     // many-to-many with frameworks
  evidenceIds: string[];           // links to Evidence Vault
  lastTestedAt: string | null;
  testFrequency: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';
  nextReviewAt: string;
  failureCount30d: number;
  passCount30d: number;
  remediationDeadline: string | null;
  notes: string;
  argusAiPrimitive: string | null;  // which ArgusAI primitive provides this evidence
  testProcedure: string;
  expectedResult: string;
  lastResult: string | null;
}

// ─── Evidence Vault ─────────────────────────────────────────────────────────

export type EvidenceType =
  | 'configuration'         // screenshots, exported configs, IaC
  | 'log'                   // access logs, audit logs, scan results
  | 'policy'                // written policies, procedures
  | 'training'              // certificates, acknowledgements
  | 'vendor'                // BAAs, DPAs, vendor SOC 2 reports
  | 'test'                  // pen tests, vuln scans, DR tests
  | 'risk'                  // risk register, DPIAs
  | 'personnel'             // background checks, NDAs
  | 'verification'          // US-Person docs, clearances
  | 'incident'              // tickets, post-mortems
  | 'ai'                    // model cards, bias reports
  | 'approval';             // change tickets, deployment approvals

export type EvidenceSource = 'manual_upload' | 'auto_collected' | 'api_pull' | 'screenshot' | 'argus_primitive';

export interface Evidence {
  id: string;
  title: string;
  description: string;
  type: EvidenceType;
  source: EvidenceSource;
  fileName: string | null;
  fileSize: number | null;        // bytes
  mimeType: string | null;
  uploadedByUserId: string;
  uploadedByName: string;
  uploadedAt: string;
  effectiveDate: string;
  expirationDate: string | null;
  reviewCycle: 'never' | 'annual' | 'quarterly' | 'monthly' | 'weekly';
  nextReviewAt: string | null;
  tags: string[];
  controlMappings: ControlMapping[];   // many-to-many with controls/frameworks
  ownerUserId: string;
  ownerName: string;
  auditorVisible: boolean;
  redactedVersion: boolean;
  versionNumber: number;
  previousVersionId: string | null;
  chainOfCustody: Array<{ userId: string; userName: string; action: string; timestamp: string }>;
  argusAiPrimitive: string | null;     // 'faithfulness_verifier' | 'classification_gate' | etc.
  status: 'current' | 'expired' | 'expiring_soon' | 'superseded';
}

// ─── AI Systems ─────────────────────────────────────────────────────────────

export type AiSystemType = 'agent' | 'model' | 'pipeline' | 'tool' | 'fine_tuned_adapter';
export type AiRiskTier = 'minimal' | 'limited' | 'high' | 'unacceptable';
export type AiSystemStatus = 'in_development' | 'in_review' | 'in_production' | 'deprecated' | 'retired';
export type AiAutonomyLevel = 'human_in_loop' | 'human_on_loop' | 'human_out_of_loop';

export interface ModelCard {
  modelDescription: string;
  intendedUses: string[];
  outOfScopeUses: string[];
  performanceFactors: string[];
  metrics: Array<{ name: string; value: number | string; threshold: number | string | null }>;
  ethicalConsiderations: string[];
  caveats: string[];
  trainingData: string;
  trainingDataLineage: string[];
  quantitativeAnalyses: string[];
}

export interface AiSystem {
  id: string;
  linkedAgentId: string | null;
  name: string;
  description: string;
  type: AiSystemType;
  status: AiSystemStatus;
  riskTier: AiRiskTier;
  autonomyLevel: AiAutonomyLevel;
  intendedPurpose: string;
  ownerUserId: string;
  ownerName: string;
  ownerTeam: string;
  baseModel: string;
  fineTuningAdapterId: string | null;
  toolGrants: string[];
  knowledgeFolders: string[];
  dataInputs: string[];
  dataOutputs: string[];
  upstreamDependencies: string[];
  downstreamConsumers: string[];
  modelCard: ModelCard | null;
  conformityAssessmentDate: string | null;
  lastBiasTestDate: string | null;
  biasTestPassRate: number | null;
  hallucinationRate30d: number | null;
  injectionAttempts30d: number;
  injectionsBlocked30d: number;
  faithfulnessScore: number | null;
  approvalGatesCount: number;
  classificationsHandled: string[];
  deployedDate: string;
  retirementDate: string | null;
  monitoringEnabled: boolean;
  humanOversightDocUrl: string | null;
  stopButtonProcedure: string;
  retrainingCadence: 'never' | 'quarterly' | 'biannual' | 'annual' | 'continuous';
  frameworkRelevance: FrameworkId[];
  iconColor: string;
}

// ─── Helper Functions ───────────────────────────────────────────────────────

const daysAgo = (d: number) => new Date(Date.now() - d * 86400000).toISOString();
const daysFromNow = (d: number) => new Date(Date.now() + d * 86400000).toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 3600000).toISOString();

// ─── Framework Catalog (7 + FINOS) ──────────────────────────────────────────

export const FRAMEWORKS: Framework[] = [
  {
    id: 'itar',
    name: 'ITAR',
    shortName: 'ITAR',
    fullName: 'International Traffic in Arms Regulations',
    description: 'US export control for defense articles, technical data, and services. Strict liability for unauthorized disclosure to non-US Persons. Required for DoD subcontractors handling USML data.',
    category: 'export_control',
    authority: 'US State Department, Directorate of Defense Trade Controls (DDTC)',
    jurisdiction: 'United States (extraterritorial)',
    controlCount: 47,
    status: 'in_progress',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(420),
    activatedByUserId: 'u-marcus-chen',
    setupCompleted: true,
    setupAnswers: {
      ddtc_registration: 'M12345',
      ddtc_expiration: daysFromNow(180),
      empowered_official: 'Jane Rivera',
      compliance_officer: 'Marcus Chen',
      verification_documents: ['us_passport', 'us_birth_certificate', 'green_card', 'naturalization_certificate'],
      reverification_frequency: 'annual',
      fips_mode: true,
      dual_person_review: true,
    },
    setupProgress: 78,
    controlsImplemented: 38,
    controlsPassing: 32,
    controlsFailing: 4,
    controlsNotApplicable: 2,
    evidenceCount: 124,
    evidenceFresh: 110,
    evidenceStale: 14,
    lastAssessmentDate: daysAgo(45),
    nextAssessmentDate: daysFromNow(320),
    certificationExpiry: null,
    assessor: 'Internal Compliance Team',
    ownerUserId: 'u-jane-rivera',
    retentionYears: 5,
    updateFrequency: 'continuous',
    enforcementDate: '1976-06-30',
    iconColor: 'red',
    recentChanges: 3,
    openFindings: 4,
    riskRating: 'high',
  },
  {
    id: 'cmmc_l2',
    name: 'CMMC L2',
    shortName: 'CMMC',
    fullName: 'Cybersecurity Maturity Model Certification — Level 2',
    description: '110 practices from NIST SP 800-171. Required for DoD contractors handling CUI. Self-assessment with C3PAO third-party validation. Annual reaffirmation, 3-year recertification.',
    category: 'security',
    authority: 'US Department of Defense, Cyber AB',
    jurisdiction: 'United States (DoD supply chain)',
    controlCount: 110,
    status: 'in_progress',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(380),
    activatedByUserId: 'u-marcus-chen',
    setupCompleted: true,
    setupAnswers: {
      cmmc_level: '2',
      assessment_type: 'c3pao',
      cui_types: ['Engineering Drawings', 'Specifications', 'Source Code'],
      contract_date: daysAgo(540),
      sprs_score: '88',
      system_owner: 'Marcus Chen',
      isso: 'Priya Patel',
      configuration_baseline: 'ArgusAI default',
      incident_response_contact: 'soc@argusai.com',
      annual_reaffirmation: daysFromNow(245),
    },
    setupProgress: 64,
    controlsImplemented: 89,
    controlsPassing: 76,
    controlsFailing: 8,
    controlsNotApplicable: 5,
    evidenceCount: 287,
    evidenceFresh: 245,
    evidenceStale: 42,
    lastAssessmentDate: daysAgo(120),
    nextAssessmentDate: daysFromNow(245),
    certificationExpiry: daysFromNow(840),
    assessor: 'Schellman & Co. (C3PAO)',
    ownerUserId: 'u-marcus-chen',
    retentionYears: 3,
    updateFrequency: 'continuous',
    enforcementDate: '2025-12-16',
    iconColor: 'blue',
    recentChanges: 12,
    openFindings: 8,
    riskRating: 'critical',
  },
  {
    id: 'hipaa',
    name: 'HIPAA',
    shortName: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    description: 'US federal law protecting Protected Health Information (PHI). Security Rule requires technical, administrative, and physical safeguards. 2026 update mandates MFA, encryption-at-rest, vulnerability scanning, asset inventory.',
    category: 'healthcare',
    authority: 'US Department of Health and Human Services (HHS), Office for Civil Rights',
    jurisdiction: 'United States',
    controlCount: 78,
    status: 'audit_ready',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(450),
    activatedByUserId: 'u-elena-petrov',
    setupCompleted: true,
    setupAnswers: {
      entity_type: 'covered_entity_and_business_associate',
      security_officer: 'Marcus Chen',
      privacy_officer: 'Elena Petrov',
      risk_analysis_completed: daysAgo(89),
      business_associates: ['Coalfire', 'AWS', 'Zendesk'],
      encryption_posture: 'AES-256-GCM with FIPS 140-2 module',
      breach_contacts: 'breach@argusai.com',
      phi_retention_years: '6',
      workforce_training_cadence: 'annual',
      contingency_test_date: daysAgo(180),
    },
    setupProgress: 95,
    controlsImplemented: 75,
    controlsPassing: 71,
    controlsFailing: 2,
    controlsNotApplicable: 2,
    evidenceCount: 198,
    evidenceFresh: 180,
    evidenceStale: 18,
    lastAssessmentDate: daysAgo(89),
    nextAssessmentDate: daysFromNow(276),
    certificationExpiry: null,
    assessor: 'Coalfire (HIPAA assessor)',
    ownerUserId: 'u-elena-petrov',
    retentionYears: 6,
    updateFrequency: 'monthly',
    enforcementDate: '1996-08-21',
    iconColor: 'emerald',
    recentChanges: 5,
    openFindings: 2,
    riskRating: 'medium',
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    shortName: 'GDPR',
    fullName: 'General Data Protection Regulation',
    description: 'EU privacy regulation. 99 articles covering lawful processing, data subject rights, breach notification (72 hours), DPIAs, cross-border transfers. Fines up to 4% of global revenue or €20M.',
    category: 'privacy',
    authority: 'European Data Protection Board, national supervisory authorities',
    jurisdiction: 'European Union (extraterritorial for EU resident data)',
    controlCount: 56,
    status: 'in_progress',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(365),
    activatedByUserId: 'u-sophie-laurent',
    setupCompleted: true,
    setupAnswers: {
      processes_eu_data: true,
      requires_dpo: true,
      lawful_bases: ['consent', 'contract', 'legitimate_interest'],
      data_storage_region: 'EU (Frankfurt)',
      cross_border_countries: ['US', 'UK'],
      transfer_mechanism: 'SCCs',
      special_category_data: true,
      childrens_data: false,
      automated_decision_making: false,
      dpo_contact: 'dpo@argusai.com',
      supervisory_authority: 'BfDI (Germany)',
      dsar_sla_days: '30',
    },
    setupProgress: 71,
    controlsImplemented: 42,
    controlsPassing: 38,
    controlsFailing: 3,
    controlsNotApplicable: 1,
    evidenceCount: 156,
    evidenceFresh: 134,
    evidenceStale: 22,
    lastAssessmentDate: daysAgo(67),
    nextAssessmentDate: daysFromNow(298),
    certificationExpiry: null,
    assessor: 'OneTrust DPA Services',
    ownerUserId: 'u-sophie-laurent',
    retentionYears: 99,            // GDPR retention is contextual; placeholder for "lifetime"
    updateFrequency: 'continuous',
    enforcementDate: '2018-05-25',
    iconColor: 'violet',
    recentChanges: 7,
    openFindings: 3,
    riskRating: 'high',
  },
  {
    id: 'soc2',
    name: 'SOC 2 Type II',
    shortName: 'SOC 2',
    fullName: 'Service Organization Control 2 — Type II',
    description: 'AICPA Trust Services Criteria covering Security, Availability, Processing Integrity, Confidentiality, Privacy. Type II reports test operating effectiveness over 6-12 month period. Annual recertification.',
    category: 'security',
    authority: 'American Institute of Certified Public Accountants (AICPA)',
    jurisdiction: 'United States (universal enterprise standard)',
    controlCount: 64,
    status: 'audit_ready',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(540),
    activatedByUserId: 'u-marcus-chen',
    setupCompleted: true,
    setupAnswers: {
      tsc_scope: ['security', 'availability', 'confidentiality'],
      report_type: 'type_2',
      audit_period_start: daysAgo(180),
      audit_period_end: daysAgo(0),
      cpa_firm: 'Schellman & Co.',
      system_boundary: 'ArgusAI SaaS platform (api, web, workers, mcp gateway)',
      customer_commitments_published: true,
      subservice_orgs: 'carve-out (AWS, Qdrant Cloud)',
      existing_risk_register: true,
      monitoring_tools: ['Datadog', 'PagerDuty', 'AWS GuardDuty'],
    },
    setupProgress: 92,
    controlsImplemented: 60,
    controlsPassing: 58,
    controlsFailing: 1,
    controlsNotApplicable: 3,
    evidenceCount: 312,
    evidenceFresh: 298,
    evidenceStale: 14,
    lastAssessmentDate: daysAgo(180),
    nextAssessmentDate: daysFromNow(185),
    certificationExpiry: daysFromNow(185),
    assessor: 'Schellman & Co.',
    ownerUserId: 'u-marcus-chen',
    retentionYears: 7,
    updateFrequency: 'continuous',
    enforcementDate: '2017-05-01',
    iconColor: 'cyan',
    recentChanges: 4,
    openFindings: 1,
    riskRating: 'low',
  },
  {
    id: 'iso27001',
    name: 'ISO 27001:2022',
    shortName: 'ISO 27001',
    fullName: 'ISO/IEC 27001:2022 — Information Security Management Systems',
    description: '93 Annex A controls across 4 themes: Organizational, People, Physical, Technological. Requires Information Security Management System (ISMS), Statement of Applicability (SoA), risk treatment plan. 3-year certification cycle.',
    category: 'security',
    authority: 'International Organization for Standardization',
    jurisdiction: 'International',
    controlCount: 93,
    status: 'in_progress',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(600),
    activatedByUserId: 'u-marcus-chen',
    setupCompleted: true,
    setupAnswers: {
      isms_scope: 'All information assets and processes of ArgusAI',
      soa_complete: true,
      risk_treatment_plan: 'Hybrid mitigation and acceptance per residual score',
      risk_owner: 'CISO (Marcus Chen)',
      certification_body: 'BSI Group',
      surveillance_cycle: 'annual',
    },
    setupProgress: 58,
    controlsImplemented: 67,
    controlsPassing: 61,
    controlsFailing: 4,
    controlsNotApplicable: 2,
    evidenceCount: 234,
    evidenceFresh: 210,
    evidenceStale: 24,
    lastAssessmentDate: daysAgo(220),
    nextAssessmentDate: daysFromNow(145),
    certificationExpiry: daysFromNow(510),
    assessor: 'BSI Group',
    ownerUserId: 'u-marcus-chen',
    retentionYears: 7,
    updateFrequency: 'monthly',
    enforcementDate: '2022-10-25',
    iconColor: 'indigo',
    recentChanges: 6,
    openFindings: 4,
    riskRating: 'medium',
  },
  {
    id: 'iso42001',
    name: 'ISO 42001',
    shortName: 'ISO 42001',
    fullName: 'ISO/IEC 42001:2023 — AI Management System',
    description: 'First international standard for AI management systems. 38 Annex A controls covering AI lifecycle, risk management, third-party AI, data governance. Mirrors ISO 27001 PDCA structure but AI-specific.',
    category: 'ai_governance',
    authority: 'International Organization for Standardization',
    jurisdiction: 'International',
    controlCount: 38,
    status: 'setup_in_progress',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(60),
    activatedByUserId: 'u-priya-patel',
    setupCompleted: false,     // still in setup wizard
    setupAnswers: {
      aims_scope: 'All AI systems deployed in ArgusAI production',
      ai_risk_owner: 'Priya Patel',
      model_card_required: true,
      lifecycle_policy: 'Retraining every 90 days or on significant drift',
    },
    setupProgress: 32,
    controlsImplemented: 18,
    controlsPassing: 15,
    controlsFailing: 2,
    controlsNotApplicable: 1,
    evidenceCount: 67,
    evidenceFresh: 60,
    evidenceStale: 7,
    lastAssessmentDate: null,
    nextAssessmentDate: daysFromNow(180),
    certificationExpiry: null,
    assessor: null,
    ownerUserId: 'u-priya-patel',
    retentionYears: 7,
    updateFrequency: 'monthly',
    enforcementDate: '2023-12-18',
    iconColor: 'pink',
    recentChanges: 9,
    openFindings: 2,
    riskRating: 'medium',
  },
  {
    id: 'finos_ai_v2',
    name: 'FINOS AI Gov v2',
    shortName: 'FINOS',
    fullName: 'FINOS AI Governance Framework v2',
    description: 'Open-source framework for LLM, RAG, and agentic AI in regulated enterprises. 25 risks / 25 controls covering data poisoning, prompt injection, memory poisoning, persistent agent compromise, hallucination, RBAC. Direct fit for ArgusAI architecture.',
    category: 'ai_governance',
    authority: 'Fintech Open Source Foundation (FINOS)',
    jurisdiction: 'International (financial services origin)',
    controlCount: 25,
    status: 'audit_ready',
    licensed: true,
    enabled: true,
    activatedAt: daysAgo(120),
    activatedByUserId: 'u-priya-patel',
    setupCompleted: true,
    setupAnswers: {
      hallucination_threshold: '2%',
      injection_defense_layers: ['input_normalization', 'regex_scan', 'llm_classifier', 'topic_drift'],
      rbac_enforcement: 'per-tenant RLS + per-user permission passthrough',
      memory_protection: 'classification_gate on all writes',
    },
    setupProgress: 88,
    controlsImplemented: 24,
    controlsPassing: 22,
    controlsFailing: 1,
    controlsNotApplicable: 1,
    evidenceCount: 89,
    evidenceFresh: 85,
    evidenceStale: 4,
    lastAssessmentDate: daysAgo(60),
    nextAssessmentDate: daysFromNow(305),
    certificationExpiry: null,
    assessor: 'Self-assessment',
    ownerUserId: 'u-priya-patel',
    retentionYears: 5,
    updateFrequency: 'continuous',
    enforcementDate: '2024-03-15',
    iconColor: 'amber',
    recentChanges: 2,
    openFindings: 1,
    riskRating: 'low',
  },

  // ─── Licensed but NOT yet activated (tenant admin can click "Add Framework") ─────
  {
    id: 'nist_ai_rmf',
    name: 'NIST AI RMF',
    shortName: 'NIST AI RMF',
    fullName: 'NIST AI Risk Management Framework 1.0',
    description: 'Voluntary framework for managing risks of AI systems. 4 functions (Govern / Map / Measure / Manage). Increasingly required for US federal AI procurement. Pairs well with ISO 42001.',
    category: 'ai_governance',
    authority: 'US National Institute of Standards and Technology',
    jurisdiction: 'United States (voluntary, federal-preferred)',
    controlCount: 72,
    status: 'not_started',
    licensed: true,
    enabled: false,
    activatedAt: null,
    activatedByUserId: null,
    setupCompleted: false,
    setupAnswers: {},
    setupProgress: 0,
    controlsImplemented: 0,
    controlsPassing: 0,
    controlsFailing: 0,
    controlsNotApplicable: 0,
    evidenceCount: 0,
    evidenceFresh: 0,
    evidenceStale: 0,
    lastAssessmentDate: null,
    nextAssessmentDate: null,
    certificationExpiry: null,
    assessor: null,
    ownerUserId: null,
    retentionYears: 7,
    updateFrequency: 'continuous',
    enforcementDate: '2023-01-26',
    iconColor: 'teal',
    recentChanges: 0,
    openFindings: 0,
    riskRating: 'medium',
  },

  // ─── NOT licensed (sysadmin must upgrade tenant contract) ─────────────────────
  {
    id: 'fedramp_moderate',
    name: 'FedRAMP Moderate',
    shortName: 'FedRAMP',
    fullName: 'Federal Risk and Authorization Management Program — Moderate Baseline',
    description: 'Required for cloud services used by US federal agencies. 325 controls from NIST SP 800-53 Rev 5. Authorization via a 3PAO assessment and Joint Authorization Board (JAB). 18-24 month typical readiness.',
    category: 'security',
    authority: 'US Federal Risk and Authorization Management Program (GSA)',
    jurisdiction: 'United States (federal)',
    controlCount: 325,
    status: 'not_started',
    licensed: false,
    enabled: false,
    activatedAt: null,
    activatedByUserId: null,
    setupCompleted: false,
    setupAnswers: {},
    setupProgress: 0,
    controlsImplemented: 0,
    controlsPassing: 0,
    controlsFailing: 0,
    controlsNotApplicable: 0,
    evidenceCount: 0,
    evidenceFresh: 0,
    evidenceStale: 0,
    lastAssessmentDate: null,
    nextAssessmentDate: null,
    certificationExpiry: null,
    assessor: null,
    ownerUserId: null,
    retentionYears: 3,
    updateFrequency: 'continuous',
    enforcementDate: '2012-06-01',
    iconColor: 'indigo',
    recentChanges: 0,
    openFindings: 0,
    riskRating: 'medium',
  },
  {
    id: 'pci_dss',
    name: 'PCI DSS v4.0',
    shortName: 'PCI DSS',
    fullName: 'Payment Card Industry Data Security Standard v4.0',
    description: 'Required for merchants and processors handling payment card data. 12 requirements, 400+ sub-requirements. Annual attestation. Not generally applicable to AI platforms unless payment card data is processed.',
    category: 'security',
    authority: 'PCI Security Standards Council',
    jurisdiction: 'International (payment card industry)',
    controlCount: 78,
    status: 'not_started',
    licensed: false,
    enabled: false,
    activatedAt: null,
    activatedByUserId: null,
    setupCompleted: false,
    setupAnswers: {},
    setupProgress: 0,
    controlsImplemented: 0,
    controlsPassing: 0,
    controlsFailing: 0,
    controlsNotApplicable: 0,
    evidenceCount: 0,
    evidenceFresh: 0,
    evidenceStale: 0,
    lastAssessmentDate: null,
    nextAssessmentDate: null,
    certificationExpiry: null,
    assessor: null,
    ownerUserId: null,
    retentionYears: 3,
    updateFrequency: 'continuous',
    enforcementDate: '2022-03-31',
    iconColor: 'rose',
    recentChanges: 0,
    openFindings: 0,
    riskRating: 'low',
  },
];

// ─── Sample Controls (representative subset across all 7 frameworks) ────────

export const CONTROLS: Control[] = [
  {
    id: 'ctrl-encryption-at-rest',
    name: 'AES-256 encryption at rest for all data stores',
    description: 'All persistent data (PostgreSQL, Qdrant, Valkey, file uploads) is encrypted at rest using AES-256 with FIPS 140-2 validated cryptographic modules.',
    family: 'system_communications',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    mappings: [
      { framework: 'hipaa', controlId: 'HIPAA:164.312(a)(2)(iv)', controlName: 'Encryption and Decryption', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:SC.L2-3.13.11', controlName: 'CUI Encryption', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.24', controlName: 'Use of cryptography', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'supporting' },
      { framework: 'gdpr', controlId: 'GDPR:Art.32', controlName: 'Security of processing', relevance: 'primary' },
      { framework: 'itar', controlId: 'ITAR:120.54', controlName: 'Encryption exemption', relevance: 'supporting' },
    ],
    evidenceIds: ['ev-encryption-config-001', 'ev-fips-cert-002', 'ev-key-rotation-003'],
    lastTestedAt: hoursAgo(4),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(180),
    failureCount30d: 0,
    passCount30d: 720,
    remediationDeadline: null,
    notes: 'Continuous monitoring via key-rotation health check. FIPS 140-2 cert renewed 2026-01-15.',
    argusAiPrimitive: 'field_encryption',
    testProcedure: 'Verify pg_settings shows AES-256, Qdrant uses encryption, secrets stored via SOPS+age. Confirm FIPS module loaded.',
    expectedResult: 'All data stores report AES-256 enabled with FIPS-validated module.',
    lastResult: 'PASS — all 4 data stores confirmed AES-256-GCM with FIPS module v3.0.8',
  },
  {
    id: 'ctrl-mfa-enforcement',
    name: 'MFA required for all users with privileged access',
    description: 'Multi-factor authentication enforced for users with admin, power_user, or compliance officer roles. TOTP, WebAuthn, or SMS supported.',
    family: 'identification_authentication',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    mappings: [
      { framework: 'cmmc_l2', controlId: 'CMMC:IA.L2-3.5.3', controlName: 'Multifactor authentication', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.312(d)', controlName: 'Person or Entity Authentication', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.5', controlName: 'Secure authentication', relevance: 'primary' },
    ],
    evidenceIds: ['ev-mfa-policy-004', 'ev-mfa-enrollment-005'],
    lastTestedAt: hoursAgo(2),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(90),
    failureCount30d: 0,
    passCount30d: 720,
    remediationDeadline: null,
    notes: '100% privileged user enrollment. Recovery codes stored in encrypted vault.',
    argusAiPrimitive: 'auth_service',
    testProcedure: 'Query users WHERE role IN (admin, power_user) AND mfa_enabled = false. Expect 0 rows.',
    expectedResult: 'Zero privileged users without MFA.',
    lastResult: 'PASS — 47/47 privileged users have MFA enabled',
  },
  {
    id: 'ctrl-tenant-rls',
    name: 'Row-level security on all tenant-scoped tables',
    description: 'PostgreSQL RLS policies enforce tenant isolation. Every query scoped by current_setting(app.current_tenant_id). Cross-tenant access impossible at database layer.',
    family: 'access_control',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    mappings: [
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.3', controlName: 'Information access restriction', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.312(a)(1)', controlName: 'Access Control', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:AC.L2-3.1.1', controlName: 'Authorized access control', relevance: 'primary' },
      { framework: 'gdpr', controlId: 'GDPR:Art.32', controlName: 'Security of processing', relevance: 'primary' },
      { framework: 'finos_ai_v2', controlId: 'FINOS:RAG-2', controlName: 'Misconfigured RBAC on retrieval', relevance: 'primary' },
    ],
    evidenceIds: ['ev-rls-config-006', 'ev-rls-test-007'],
    lastTestedAt: hoursAgo(1),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(30),
    failureCount30d: 0,
    passCount30d: 720,
    remediationDeadline: null,
    notes: 'Automated cross-tenant test runs every hour. Failure pages on-call immediately.',
    argusAiPrimitive: 'rls_middleware',
    testProcedure: 'Authenticated as Tenant A, attempt to SELECT FROM all tables WHERE tenant_id = Tenant B. Expect 0 rows for every table.',
    expectedResult: 'Zero cross-tenant rows returned across all 23 RLS-enabled tables.',
    lastResult: 'PASS — 0/0 cross-tenant rows across 23 tables',
  },
  {
    id: 'ctrl-audit-hash-chain',
    name: 'Hash-chained tamper-evident audit trail',
    description: 'Every audit event hashed with SHA-256 of (event_data + previous_hash). Chain integrity verifiable end-to-end. Required for ITAR (5y) and CMMC (3y) retention.',
    family: 'audit_accountability',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    mappings: [
      { framework: 'itar', controlId: 'ITAR:122.5', controlName: 'Records Retention', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:AU.L2-3.3.1', controlName: 'Audit logs', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:AU.L2-3.3.8', controlName: 'Audit log protection', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.312(b)', controlName: 'Audit controls', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC7.2', controlName: 'System monitoring', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.15', controlName: 'Logging', relevance: 'primary' },
    ],
    evidenceIds: ['ev-audit-chain-008', 'ev-chain-verification-009'],
    lastTestedAt: hoursAgo(6),
    testFrequency: 'daily',
    nextReviewAt: daysFromNow(7),
    failureCount30d: 0,
    passCount30d: 30,
    remediationDeadline: null,
    notes: 'Chain verification runs daily. Last verified at sequence 4,827,291.',
    argusAiPrimitive: 'audit_service',
    testProcedure: 'Walk audit_logs in sequence order, recompute hash of each row, compare against stored hash. Verify chain unbroken from sequence 1 to current.',
    expectedResult: 'All hashes match, no gaps in sequence numbers.',
    lastResult: 'PASS — verified 4,827,291 records, no breaks',
  },
  {
    id: 'ctrl-classification-gate',
    name: 'Classification gate on all data paths to LLM',
    description: 'Every retrieval (RAG, MCP tool, memory, document view) passes through ClassificationGate that filters by user clearance BEFORE reaching the LLM. ITAR data never visible to non-US-Persons.',
    family: 'export_controls',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-jane-rivera',
    ownerName: 'Jane Rivera',
    mappings: [
      { framework: 'itar', controlId: 'ITAR:120.17', controlName: 'Deemed export prevention', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:AC.L2-3.1.3', controlName: 'CUI flow control', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.308(a)(4)', controlName: 'Information Access Management', relevance: 'primary' },
      { framework: 'finos_ai_v2', controlId: 'FINOS:DATA-3', controlName: 'Data leakage in retrieval', relevance: 'primary' },
    ],
    evidenceIds: ['ev-classification-config-010', 'ev-gate-test-results-011'],
    lastTestedAt: hoursAgo(1),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(30),
    failureCount30d: 0,
    passCount30d: 720,
    remediationDeadline: null,
    notes: 'Per ADR-012. Active on RAG, tools, memory, and document view paths.',
    argusAiPrimitive: 'classification_gate',
    testProcedure: 'Synthetic non-US-Person session attempts to retrieve ITAR document. Expect denial + audit log entry.',
    expectedResult: 'Retrieval blocked, denial reason logged, no ITAR data in LLM context.',
    lastResult: 'PASS — 12,847 attempts in 30d, 100% correctly classified',
  },
  {
    id: 'ctrl-us-person-verification',
    name: 'US Person verification with documentary evidence',
    description: 'Users accessing ITAR data must be verified as US Persons via documentary evidence (passport, birth certificate, green card, naturalization certificate). NOT self-declaration. Re-verification annually.',
    family: 'identification_authentication',
    status: 'partial',
    automation: 'manual',
    priority: 'critical',
    ownerUserId: 'u-jane-rivera',
    ownerName: 'Jane Rivera',
    mappings: [
      { framework: 'itar', controlId: 'ITAR:120.62', controlName: 'US Person definition', relevance: 'primary' },
    ],
    evidenceIds: ['ev-us-person-process-012', 'ev-verifications-roster-013'],
    lastTestedAt: daysAgo(7),
    testFrequency: 'weekly',
    nextReviewAt: daysFromNow(7),
    failureCount30d: 1,
    passCount30d: 3,
    remediationDeadline: daysFromNow(14),
    notes: 'One user has expired verification. Renewal package sent 2026-04-12.',
    argusAiPrimitive: 'verification_service',
    testProcedure: 'Audit users with ITAR access. Verify each has unexpired US Person verification with documentary evidence stored.',
    expectedResult: 'All ITAR-access users have current verification with stored evidence.',
    lastResult: 'PARTIAL — 14/15 verified; 1 expired (user: kelly.morrison@argusai.com)',
  },
  {
    id: 'ctrl-faithfulness-verification',
    name: 'Hallucination prevention via faithfulness verifier',
    description: 'DeBERTa NLI verifier checks every generated claim against its cited source. Claims failing NLI are flagged or removed before reaching user. Tracks hallucination rate over time.',
    family: 'ai_governance',
    status: 'passing',
    automation: 'automated',
    priority: 'high',
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    mappings: [
      { framework: 'finos_ai_v2', controlId: 'FINOS:LLM-1', controlName: 'Hallucination', relevance: 'primary' },
      { framework: 'iso42001', controlId: 'ISO42001:A.6.2.4', controlName: 'AI system performance', relevance: 'primary' },
    ],
    evidenceIds: ['ev-faithfulness-config-014', 'ev-hallucination-metrics-015'],
    lastTestedAt: hoursAgo(1),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(30),
    failureCount30d: 2,
    passCount30d: 718,
    remediationDeadline: null,
    notes: 'Hallucination rate 0.8% over 30 days. Threshold: 2.0%.',
    argusAiPrimitive: 'faithfulness_verifier',
    testProcedure: 'Run RAGAS evaluation against golden dataset (200 Q&A pairs). Compare faithfulness score against baseline.',
    expectedResult: 'Faithfulness score >= 0.92, hallucination rate < 2%.',
    lastResult: 'PASS — faithfulness 0.94, hallucination rate 0.8%',
  },
  {
    id: 'ctrl-injection-defense',
    name: '4-layer prompt injection defense',
    description: 'Input normalization (NFKC, zero-width strip) + regex pattern scan + batched LLM classifier + topic drift detection. Tracks blocked injection attempts.',
    family: 'system_information_integrity',
    status: 'passing',
    automation: 'automated',
    priority: 'critical',
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    mappings: [
      { framework: 'finos_ai_v2', controlId: 'FINOS:LLM-2', controlName: 'Prompt injection', relevance: 'primary' },
      { framework: 'iso42001', controlId: 'ISO42001:A.6.2.7', controlName: 'AI system security', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC6.6', controlName: 'Vulnerability management', relevance: 'supporting' },
    ],
    evidenceIds: ['ev-injection-config-016', 'ev-injection-metrics-017'],
    lastTestedAt: hoursAgo(1),
    testFrequency: 'hourly',
    nextReviewAt: daysFromNow(30),
    failureCount30d: 0,
    passCount30d: 720,
    remediationDeadline: null,
    notes: '847 injection attempts blocked in 30d. 99.8% block rate.',
    argusAiPrimitive: 'injection_defense',
    testProcedure: 'Run 50 known injection patterns against /chat endpoint. Expect 100% blocked at one of the 4 layers.',
    expectedResult: 'All 50 patterns detected and blocked.',
    lastResult: 'PASS — 50/50 patterns blocked, avg latency 187ms',
  },
  {
    id: 'ctrl-gdpr-dsar-process',
    name: 'GDPR DSAR fulfillment within 30 days',
    description: 'Data Subject Access Requests (access, deletion, portability, rectification) fulfilled within 30 calendar days. Identity verification required before processing.',
    family: 'privacy_controls',
    status: 'passing',
    automation: 'hybrid',
    priority: 'high',
    ownerUserId: 'u-sophie-laurent',
    ownerName: 'Sophie Laurent',
    mappings: [
      { framework: 'gdpr', controlId: 'GDPR:Art.12', controlName: 'Transparent information', relevance: 'primary' },
      { framework: 'gdpr', controlId: 'GDPR:Art.15', controlName: 'Right of access', relevance: 'primary' },
      { framework: 'gdpr', controlId: 'GDPR:Art.17', controlName: 'Right to erasure', relevance: 'primary' },
      { framework: 'gdpr', controlId: 'GDPR:Art.20', controlName: 'Right to portability', relevance: 'primary' },
    ],
    evidenceIds: ['ev-dsar-process-018', 'ev-dsar-metrics-019'],
    lastTestedAt: daysAgo(7),
    testFrequency: 'weekly',
    nextReviewAt: daysFromNow(7),
    failureCount30d: 0,
    passCount30d: 4,
    remediationDeadline: null,
    notes: '23 DSARs in 30d, all fulfilled within 18 days average.',
    argusAiPrimitive: 'dsar_workflow',
    testProcedure: 'Audit open DSARs. Verify identity check completed within 5 days, response within 30 days.',
    expectedResult: 'No DSARs overdue. Average response time < 30 days.',
    lastResult: 'PASS — 23/23 fulfilled, avg 18 days',
  },
  {
    id: 'ctrl-breach-notification-72h',
    name: '72-hour breach notification process',
    description: 'Confirmed personal data breaches notified to supervisory authority within 72 hours (GDPR), to HHS within 60 days for >500 records (HIPAA), and to data subjects without undue delay.',
    family: 'incident_management',
    status: 'passing',
    automation: 'hybrid',
    priority: 'critical',
    ownerUserId: 'u-elena-petrov',
    ownerName: 'Elena Petrov',
    mappings: [
      { framework: 'gdpr', controlId: 'GDPR:Art.33', controlName: 'Notification of breach to authority', relevance: 'primary' },
      { framework: 'gdpr', controlId: 'GDPR:Art.34', controlName: 'Communication of breach to subject', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.404', controlName: 'Breach Notification to Individuals', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.408', controlName: 'Breach Notification to HHS', relevance: 'primary' },
    ],
    evidenceIds: ['ev-breach-process-020', 'ev-breach-tabletop-021'],
    lastTestedAt: daysAgo(45),
    testFrequency: 'quarterly',
    nextReviewAt: daysFromNow(45),
    failureCount30d: 0,
    passCount30d: 1,
    remediationDeadline: null,
    notes: 'Tabletop exercise Q1 2026 demonstrated 18-hour internal response, 36-hour authority notification.',
    argusAiPrimitive: 'breach_workflow',
    testProcedure: 'Quarterly tabletop simulating PHI breach. Measure time to detect, contain, notify.',
    expectedResult: 'Authority notification within 72 hours, all evidence preserved.',
    lastResult: 'PASS — Q1 tabletop: 36h to authority notification, all roles played correctly',
  },
  {
    id: 'ctrl-vendor-baa-tracking',
    name: 'Business Associate Agreement (BAA) tracking',
    description: 'All vendors handling PHI have signed BAA on file. BAAs reviewed annually, expirations tracked, sub-processor changes flagged.',
    family: 'vendor_management',
    status: 'passing',
    automation: 'manual',
    priority: 'high',
    ownerUserId: 'u-elena-petrov',
    ownerName: 'Elena Petrov',
    mappings: [
      { framework: 'hipaa', controlId: 'HIPAA:164.504(e)', controlName: 'Business Associate Contracts', relevance: 'primary' },
    ],
    evidenceIds: ['ev-vendor-registry-022'],
    lastTestedAt: daysAgo(15),
    testFrequency: 'monthly',
    nextReviewAt: daysFromNow(15),
    failureCount30d: 0,
    passCount30d: 1,
    remediationDeadline: null,
    notes: '8/8 PHI vendors have current BAAs. Next expiry: 2026-09-15 (Coalfire).',
    argusAiPrimitive: null,
    testProcedure: 'Filter vendors WHERE data_accessed CONTAINS PHI AND baa_status != current. Expect 0 rows.',
    expectedResult: 'All PHI vendors have current BAA.',
    lastResult: 'PASS — 8/8 vendors',
  },
  {
    id: 'ctrl-iso42001-ai-policy',
    name: 'AI system policy and intended use documentation',
    description: 'Every AI system in production has documented intended use, out-of-scope uses, performance metrics, and limitations (model card). Required by ISO 42001 A.6.2.',
    family: 'ai_governance',
    status: 'partial',
    automation: 'manual',
    priority: 'high',
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    mappings: [
      { framework: 'iso42001', controlId: 'ISO42001:A.6.2.2', controlName: 'AI system intended use', relevance: 'primary' },
      { framework: 'iso42001', controlId: 'ISO42001:A.7.2', controlName: 'Documentation', relevance: 'primary' },
      { framework: 'finos_ai_v2', controlId: 'FINOS:GOV-1', controlName: 'AI system inventory', relevance: 'primary' },
    ],
    evidenceIds: ['ev-model-cards-023'],
    lastTestedAt: daysAgo(14),
    testFrequency: 'monthly',
    nextReviewAt: daysFromNow(16),
    failureCount30d: 1,
    passCount30d: 0,
    remediationDeadline: daysFromNow(30),
    notes: '11/14 agents have complete model cards. 3 agents need bias testing data added.',
    argusAiPrimitive: 'ai_systems_inventory',
    testProcedure: 'Audit AI systems WHERE status = production. Verify model_card.intendedUses, outOfScopeUses, metrics populated.',
    expectedResult: 'All production AI systems have complete model cards.',
    lastResult: 'PARTIAL — 11/14 complete, 3 missing bias metrics',
  },
];

// ─── Sample Evidence ────────────────────────────────────────────────────────

export const EVIDENCE: Evidence[] = [
  {
    id: 'ev-encryption-config-001',
    title: 'PostgreSQL AES-256 Encryption Configuration',
    description: 'Database cluster configuration showing pg_settings with data_encryption=aes256, FIPS module loaded.',
    type: 'configuration',
    source: 'auto_collected',
    fileName: 'postgres-encryption-config-2026-04-15.json',
    fileSize: 4823,
    mimeType: 'application/json',
    uploadedByUserId: 'u-system',
    uploadedByName: 'System (auto-collected)',
    uploadedAt: hoursAgo(4),
    effectiveDate: hoursAgo(4),
    expirationDate: daysFromNow(30),
    reviewCycle: 'monthly',
    nextReviewAt: daysFromNow(30),
    tags: ['encryption', 'database', 'fips', 'auto-collected'],
    controlMappings: [
      { framework: 'hipaa', controlId: 'HIPAA:164.312(a)(2)(iv)', controlName: 'Encryption and Decryption', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:SC.L2-3.13.11', controlName: 'CUI Encryption', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.24', controlName: 'Use of cryptography', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'supporting' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 47,
    previousVersionId: 'ev-encryption-config-001-v46',
    chainOfCustody: [
      { userId: 'u-system', userName: 'System', action: 'auto_collected', timestamp: hoursAgo(4) },
      { userId: 'u-marcus-chen', userName: 'Marcus Chen', action: 'reviewed', timestamp: hoursAgo(3) },
    ],
    argusAiPrimitive: 'field_encryption',
    status: 'current',
  },
  {
    id: 'ev-fips-cert-002',
    title: 'OpenSSL FIPS 140-2 Module Certificate',
    description: 'NIST CMVP certificate #4282 for OpenSSL FIPS Module v3.0.8, valid through 2027-12-31.',
    type: 'configuration',
    source: 'manual_upload',
    fileName: 'openssl-fips-cert-4282.pdf',
    fileSize: 287402,
    mimeType: 'application/pdf',
    uploadedByUserId: 'u-marcus-chen',
    uploadedByName: 'Marcus Chen',
    uploadedAt: daysAgo(89),
    effectiveDate: '2024-01-15',
    expirationDate: '2027-12-31',
    reviewCycle: 'annual',
    nextReviewAt: daysFromNow(276),
    tags: ['fips', 'cryptography', 'certificate'],
    controlMappings: [
      { framework: 'cmmc_l2', controlId: 'CMMC:SC.L2-3.13.11', controlName: 'CUI Encryption', relevance: 'primary' },
      { framework: 'itar', controlId: 'ITAR:120.54', controlName: 'Encryption exemption', relevance: 'primary' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 1,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-marcus-chen', userName: 'Marcus Chen', action: 'uploaded', timestamp: daysAgo(89) },
    ],
    argusAiPrimitive: null,
    status: 'current',
  },
  {
    id: 'ev-rls-test-007',
    title: 'Hourly Cross-Tenant RLS Verification Report',
    description: 'Automated test attempting cross-tenant access on all 23 RLS-protected tables. Tested 720 times in last 30 days, 0 violations.',
    type: 'test',
    source: 'argus_primitive',
    fileName: 'rls-test-2026-04-19-19h.json',
    fileSize: 18234,
    mimeType: 'application/json',
    uploadedByUserId: 'u-system',
    uploadedByName: 'System (RLS test runner)',
    uploadedAt: hoursAgo(1),
    effectiveDate: hoursAgo(1),
    expirationDate: daysFromNow(7),
    reviewCycle: 'weekly',
    nextReviewAt: daysFromNow(7),
    tags: ['rls', 'tenant-isolation', 'automated-test'],
    controlMappings: [
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.3', controlName: 'Information access restriction', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.312(a)(1)', controlName: 'Access Control', relevance: 'primary' },
      { framework: 'finos_ai_v2', controlId: 'FINOS:RAG-2', controlName: 'Misconfigured RBAC on retrieval', relevance: 'primary' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 720,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-system', userName: 'System', action: 'generated', timestamp: hoursAgo(1) },
    ],
    argusAiPrimitive: 'rls_middleware',
    status: 'current',
  },
  {
    id: 'ev-audit-chain-008',
    title: 'Audit Hash Chain Verification — Sequence 4,827,291',
    description: 'End-to-end verification of audit log hash chain from sequence 1 to current. All hashes match. Generated daily.',
    type: 'log',
    source: 'argus_primitive',
    fileName: 'hash-chain-verification-2026-04-19.json',
    fileSize: 9472,
    mimeType: 'application/json',
    uploadedByUserId: 'u-system',
    uploadedByName: 'System (audit verifier)',
    uploadedAt: hoursAgo(6),
    effectiveDate: hoursAgo(6),
    expirationDate: daysFromNow(1),
    reviewCycle: 'weekly',
    nextReviewAt: daysFromNow(7),
    tags: ['audit', 'hash-chain', 'integrity'],
    controlMappings: [
      { framework: 'itar', controlId: 'ITAR:122.5', controlName: 'Records Retention', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:AU.L2-3.3.8', controlName: 'Audit log protection', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.312(b)', controlName: 'Audit controls', relevance: 'primary' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 247,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-system', userName: 'System', action: 'generated', timestamp: hoursAgo(6) },
    ],
    argusAiPrimitive: 'audit_service',
    status: 'current',
  },
  {
    id: 'ev-hallucination-metrics-015',
    title: 'Hallucination Rate Metrics — 30 Day Window',
    description: 'Faithfulness verifier output aggregated over 30 days. Hallucination rate 0.8% (target < 2%). Per-agent breakdown included.',
    type: 'ai',
    source: 'argus_primitive',
    fileName: 'hallucination-metrics-2026-04-19.json',
    fileSize: 14092,
    mimeType: 'application/json',
    uploadedByUserId: 'u-system',
    uploadedByName: 'System (faithfulness verifier)',
    uploadedAt: hoursAgo(1),
    effectiveDate: hoursAgo(1),
    expirationDate: daysFromNow(7),
    reviewCycle: 'weekly',
    nextReviewAt: daysFromNow(7),
    tags: ['ai', 'hallucination', 'faithfulness', 'finos'],
    controlMappings: [
      { framework: 'finos_ai_v2', controlId: 'FINOS:LLM-1', controlName: 'Hallucination', relevance: 'primary' },
      { framework: 'iso42001', controlId: 'ISO42001:A.6.2.4', controlName: 'AI system performance', relevance: 'primary' },
    ],
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 30,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-system', userName: 'System', action: 'generated', timestamp: hoursAgo(1) },
    ],
    argusAiPrimitive: 'faithfulness_verifier',
    status: 'current',
  },
  {
    id: 'ev-injection-metrics-017',
    title: 'Prompt Injection Defense Metrics — 30 Day Window',
    description: '847 injection attempts blocked across 4 defense layers. Per-layer breakdown: regex 312, LLM classifier 421, topic drift 89, second model 25.',
    type: 'ai',
    source: 'argus_primitive',
    fileName: 'injection-metrics-2026-04-19.json',
    fileSize: 11874,
    mimeType: 'application/json',
    uploadedByUserId: 'u-system',
    uploadedByName: 'System (injection defense)',
    uploadedAt: hoursAgo(1),
    effectiveDate: hoursAgo(1),
    expirationDate: daysFromNow(7),
    reviewCycle: 'weekly',
    nextReviewAt: daysFromNow(7),
    tags: ['ai', 'injection', 'security', 'finos'],
    controlMappings: [
      { framework: 'finos_ai_v2', controlId: 'FINOS:LLM-2', controlName: 'Prompt injection', relevance: 'primary' },
      { framework: 'iso42001', controlId: 'ISO42001:A.6.2.7', controlName: 'AI system security', relevance: 'primary' },
    ],
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 30,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-system', userName: 'System', action: 'generated', timestamp: hoursAgo(1) },
    ],
    argusAiPrimitive: 'injection_defense',
    status: 'current',
  },
  {
    id: 'ev-mfa-policy-004',
    title: 'Multi-Factor Authentication Policy v3.2',
    description: 'Enterprise MFA policy requiring TOTP/WebAuthn/SMS for all privileged users. Approved by CISO.',
    type: 'policy',
    source: 'manual_upload',
    fileName: 'mfa-policy-v3.2.pdf',
    fileSize: 234876,
    mimeType: 'application/pdf',
    uploadedByUserId: 'u-marcus-chen',
    uploadedByName: 'Marcus Chen',
    uploadedAt: daysAgo(45),
    effectiveDate: daysAgo(45),
    expirationDate: daysFromNow(320),
    reviewCycle: 'annual',
    nextReviewAt: daysFromNow(320),
    tags: ['policy', 'mfa', 'authentication'],
    controlMappings: [
      { framework: 'soc2', controlId: 'SOC2:CC6.1', controlName: 'Logical Access', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.5', controlName: 'Secure authentication', relevance: 'primary' },
      { framework: 'cmmc_l2', controlId: 'CMMC:IA.L2-3.5.3', controlName: 'Multifactor authentication', relevance: 'primary' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 3,
    previousVersionId: 'ev-mfa-policy-004-v2',
    chainOfCustody: [
      { userId: 'u-marcus-chen', userName: 'Marcus Chen', action: 'uploaded', timestamp: daysAgo(45) },
      { userId: 'u-elena-petrov', userName: 'Elena Petrov', action: 'reviewed', timestamp: daysAgo(43) },
    ],
    argusAiPrimitive: null,
    status: 'current',
  },
  {
    id: 'ev-pen-test-2026-q1',
    title: 'Q1 2026 Penetration Test Report',
    description: 'Annual penetration test by Bishop Fox. 2 high findings (remediated), 5 medium (3 remediated, 2 in progress), 12 informational.',
    type: 'test',
    source: 'manual_upload',
    fileName: 'pen-test-q1-2026.pdf',
    fileSize: 4827341,
    mimeType: 'application/pdf',
    uploadedByUserId: 'u-marcus-chen',
    uploadedByName: 'Marcus Chen',
    uploadedAt: daysAgo(67),
    effectiveDate: daysAgo(67),
    expirationDate: daysFromNow(298),
    reviewCycle: 'annual',
    nextReviewAt: daysFromNow(298),
    tags: ['pen-test', 'security-assessment', 'bishop-fox'],
    controlMappings: [
      { framework: 'cmmc_l2', controlId: 'CMMC:RA.L2-3.11.2', controlName: 'Vulnerability scanning', relevance: 'primary' },
      { framework: 'soc2', controlId: 'SOC2:CC4.1', controlName: 'COSO Monitoring', relevance: 'primary' },
      { framework: 'iso27001', controlId: 'ISO27001:A.8.8', controlName: 'Vulnerability management', relevance: 'primary' },
      { framework: 'hipaa', controlId: 'HIPAA:164.308(a)(8)', controlName: 'Evaluation', relevance: 'primary' },
    ],
    ownerUserId: 'u-marcus-chen',
    ownerName: 'Marcus Chen',
    auditorVisible: true,
    redactedVersion: false,
    versionNumber: 1,
    previousVersionId: null,
    chainOfCustody: [
      { userId: 'u-marcus-chen', userName: 'Marcus Chen', action: 'uploaded', timestamp: daysAgo(67) },
    ],
    argusAiPrimitive: null,
    status: 'current',
  },
];
// ─── Sample AI Systems ──────────────────────────────────────────────────────

export const AI_SYSTEMS: AiSystem[] = [
  {
    id: 'ai-ask-argus',
    linkedAgentId: 'agent-ask-argus',
    name: 'Ask Argus (Supervisor Agent)',
    description: 'Primary user-facing conversational interface. Manages conversation state, clarification questions, response formatting, handoffs to specialist agents.',
    type: 'agent',
    status: 'in_production',
    riskTier: 'limited',
    autonomyLevel: 'human_in_loop',
    intendedPurpose: 'Provide cited, verified answers to industrial operations questions by orchestrating specialist agents and RAG retrieval.',
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    ownerTeam: 'AI Platform',
    baseModel: 'qwen2.5-72b-awq',
    fineTuningAdapterId: null,
    toolGrants: ['iq.asset.search', 'iq.cmms.getWorkOrders', 'iq.telemetry.getHistory', 'builtin.search_knowledge', 'builtin.cite_source'],
    knowledgeFolders: ['folder-all'],
    dataInputs: ['User queries', 'Conversation history', 'Document chunks (RAG)', 'MCP tool results'],
    dataOutputs: ['Cited answers', 'Clarification questions', 'Tool invocations'],
    upstreamDependencies: [],
    downstreamConsumers: ['ai-maintenance-intel', 'ai-troubleshooting', 'ai-incident-analysis', 'ai-documentation'],
    modelCard: {
      modelDescription: 'Qwen2.5-72B AWQ-quantized base model, no fine-tuning. Operated as supervisor agent with skill-bundled tool grants.',
      intendedUses: [
        'Answer questions about facility operations and equipment',
        'Search and cite engineering documentation',
        'Coordinate multi-step diagnostic queries',
        'Route to specialist agents when domain expertise needed',
      ],
      outOfScopeUses: [
        'Clinical or medical decision support',
        'Legal advice',
        'Autonomous action without approval gate',
        'Real-time control of safety-critical equipment',
      ],
      performanceFactors: [
        'Quality of cited documentation',
        'Specificity of equipment context',
        'Network latency to MCP servers',
        'GPU availability for inference',
      ],
      metrics: [
        { name: 'RAGAS Faithfulness', value: 0.94, threshold: 0.92 },
        { name: 'Answer Relevancy', value: 0.91, threshold: 0.85 },
        { name: 'Context Precision', value: 0.88, threshold: 0.80 },
        { name: 'Hallucination Rate (30d)', value: '0.8%', threshold: '< 2%' },
        { name: 'Avg First Token Latency', value: '1.4s', threshold: '< 2s' },
      ],
      ethicalConsiderations: [
        'Industrial decisions on hallucinated data cause safety incidents',
        'AI must defer to human expertise on safety-critical procedures',
        'Citation transparency required for all responses',
      ],
      caveats: [
        'Performance degrades on out-of-domain questions',
        'Edge cases in legacy equipment may lack training data',
        'Multi-language support limited to English and Spanish',
      ],
      trainingData: 'Base model trained on Qwen Foundation public corpus. No customer data used for training.',
      trainingDataLineage: ['Qwen Foundation Public Dataset v2.5', 'No fine-tuning adapter applied'],
      quantitativeAnalyses: [
        'Per-domain faithfulness: maintenance 0.95, safety 0.97, compliance 0.91',
        'Per-language: English 0.94, Spanish 0.89',
      ],
    },
    conformityAssessmentDate: daysAgo(60),
    lastBiasTestDate: daysAgo(30),
    biasTestPassRate: 0.96,
    hallucinationRate30d: 0.008,
    injectionAttempts30d: 312,
    injectionsBlocked30d: 312,
    faithfulnessScore: 0.94,
    approvalGatesCount: 4,
    classificationsHandled: ['Unclassified', 'CUI', 'PHI'],
    deployedDate: daysAgo(180),
    retirementDate: null,
    monitoringEnabled: true,
    humanOversightDocUrl: '/policies/human-oversight-procedure-v2.pdf',
    stopButtonProcedure: 'Admin: /admin/agents → Ask Argus → Pause. Per-tenant kill switch via feature toggle.',
    retrainingCadence: 'never',
    frameworkRelevance: ['iso42001', 'finos_ai_v2'],
    iconColor: 'eye',
  },
  {
    id: 'ai-maintenance-intel',
    linkedAgentId: 'agent-maintenance',
    name: 'Maintenance Intelligence Agent',
    description: 'Equipment health analysis, maintenance forecasting, PM scheduling. Specializes in vibration analysis, oil analysis, thermal imaging.',
    type: 'agent',
    status: 'in_production',
    riskTier: 'limited',
    autonomyLevel: 'human_in_loop',
    intendedPurpose: 'Diagnose equipment issues, predict failures, recommend maintenance interventions backed by sensor data and engineering documentation.',
    ownerUserId: 'u-priya-patel',
    ownerName: 'Priya Patel',
    ownerTeam: 'AI Platform',
    baseModel: 'qwen2.5-72b-awq',
    fineTuningAdapterId: 'lora-maintenance-v3',
    toolGrants: ['iq.asset.search', 'iq.asset.getHistory', 'iq.telemetry.getHistory', 'iq.cmms.createWorkOrderDraft'],
    knowledgeFolders: ['folder-eng-manuals', 'folder-maintenance-sops'],
    dataInputs: ['Equipment tags', 'Telemetry time series', 'Maintenance history', 'OEM manuals'],
    dataOutputs: ['Diagnoses', 'Severity assessments', 'Recommended actions', 'Work order drafts'],
    upstreamDependencies: ['ai-ask-argus'],
    downstreamConsumers: [],
    modelCard: {
      modelDescription: 'Qwen2.5-72B with LoRA adapter fine-tuned on 18,000 maintenance scenarios.',
      intendedUses: [
        'Diagnose mechanical and electrical equipment issues',
        'Forecast time-to-failure for rotating equipment',
        'Recommend preventive maintenance schedules',
        'Draft work orders for human approval',
      ],
      outOfScopeUses: [
        'Autonomous equipment shutdown',
        'Final approval of safety-critical maintenance',
        'Diagnoses without telemetry data',
      ],
      performanceFactors: [
        'Sensor data quality and frequency',
        'Equipment age and documentation completeness',
        'Match to fine-tuning training distribution',
      ],
      metrics: [
        { name: 'Diagnostic Accuracy (vs SME)', value: 0.89, threshold: 0.85 },
        { name: 'False Positive Rate', value: '6%', threshold: '< 10%' },
        { name: 'False Negative Rate', value: '4%', threshold: '< 5%' },
        { name: 'RAGAS Faithfulness', value: 0.95, threshold: 0.92 },
      ],
      ethicalConsiderations: [
        'Misdiagnosis can lead to safety incidents or production losses',
        'Recommendations must be verified by qualified maintenance staff',
      ],
      caveats: [
        'Performance lower on legacy equipment without modern sensors',
        'Limited to equipment classes in training set',
      ],
      trainingData: '18,000 historical maintenance scenarios from 4 industrial customers (anonymized). 65% rotating equipment, 25% electrical, 10% pneumatic/hydraulic.',
      trainingDataLineage: [
        'Customer 1: 6,400 scenarios (2020-2024)',
        'Customer 2: 4,800 scenarios (2021-2024)',
        'Customer 3: 4,100 scenarios (2022-2024)',
        'Customer 4: 2,700 scenarios (2023-2024)',
      ],
      quantitativeAnalyses: [
        'By equipment class: rotating 0.91, electrical 0.87, pneumatic 0.82',
        'By severity: critical 0.94, high 0.89, medium 0.85, low 0.78',
      ],
    },
    conformityAssessmentDate: daysAgo(30),
    lastBiasTestDate: daysAgo(14),
    biasTestPassRate: 0.94,
    hallucinationRate30d: 0.006,
    injectionAttempts30d: 89,
    injectionsBlocked30d: 89,
    faithfulnessScore: 0.95,
    approvalGatesCount: 2,
    classificationsHandled: ['Unclassified', 'CUI'],
    deployedDate: daysAgo(120),
    retirementDate: null,
    monitoringEnabled: true,
    humanOversightDocUrl: '/policies/human-oversight-procedure-v2.pdf',
    stopButtonProcedure: 'Admin: /admin/agents → Maintenance Intelligence → Pause. Auto-pause if hallucination rate > 2%.',
    retrainingCadence: 'quarterly',
    frameworkRelevance: ['iso42001', 'finos_ai_v2', 'cmmc_l2'],
    iconColor: 'cyan',
  },
  {
    id: 'ai-compliance-monitor',
    linkedAgentId: 'agent-compliance',
    name: 'Compliance Monitor Agent',
    description: 'ITAR/CMMC/HIPAA status checking, certification tracking, framework compliance verification.',
    type: 'agent',
    status: 'in_production',
    riskTier: 'limited',
    autonomyLevel: 'human_on_loop',
    intendedPurpose: 'Continuously monitor compliance posture across active frameworks, flag drift, generate evidence packages.',
    ownerUserId: 'u-jane-rivera',
    ownerName: 'Jane Rivera',
    ownerTeam: 'Compliance',
    baseModel: 'qwen2.5-72b-awq',
    fineTuningAdapterId: 'lora-compliance-v2',
    toolGrants: ['iq.compliance.getFrameworks', 'iq.document.search', 'builtin.search_knowledge'],
    knowledgeFolders: ['folder-compliance', 'folder-policies'],
    dataInputs: ['Framework definitions', 'Policy documents', 'Audit logs', 'Vendor agreements'],
    dataOutputs: ['Compliance scorecards', 'Gap analyses', 'Evidence requests', 'Audit reports'],
    upstreamDependencies: ['ai-ask-argus'],
    downstreamConsumers: [],
    modelCard: {
      modelDescription: 'Qwen2.5-72B with LoRA adapter fine-tuned on regulatory text and 2,400 compliance scenarios.',
      intendedUses: [
        'Continuous compliance posture monitoring',
        'Gap analysis against framework requirements',
        'Evidence package generation for auditors',
        'Drift detection on configuration changes',
      ],
      outOfScopeUses: [
        'Final compliance certification (requires human assessor)',
        'Legal interpretation of regulations',
        'Direct remediation actions without approval',
      ],
      performanceFactors: ['Quality of policy documentation', 'Completeness of audit logs'],
      metrics: [
        { name: 'Gap Detection Recall', value: 0.92, threshold: 0.90 },
        { name: 'False Positive Rate', value: '4%', threshold: '< 5%' },
        { name: 'Evidence Match Accuracy', value: 0.96, threshold: 0.94 },
      ],
      ethicalConsiderations: ['Misclassification of compliance status creates legal liability'],
      caveats: ['Updates lag behind regulatory changes by up to 30 days'],
      trainingData: '2,400 compliance scenarios across ITAR, CMMC, HIPAA, GDPR, SOC 2.',
      trainingDataLineage: ['NIST SP 800-171 corpus', '2,400 expert-labeled compliance scenarios'],
      quantitativeAnalyses: ['By framework: ITAR 0.94, CMMC 0.93, HIPAA 0.91, GDPR 0.89, SOC 2 0.95'],
    },
    conformityAssessmentDate: daysAgo(45),
    lastBiasTestDate: daysAgo(21),
    biasTestPassRate: 0.97,
    hallucinationRate30d: 0.004,
    injectionAttempts30d: 47,
    injectionsBlocked30d: 47,
    faithfulnessScore: 0.96,
    approvalGatesCount: 3,
    classificationsHandled: ['Unclassified', 'CUI'],
    deployedDate: daysAgo(150),
    retirementDate: null,
    monitoringEnabled: true,
    humanOversightDocUrl: '/policies/human-oversight-procedure-v2.pdf',
    stopButtonProcedure: 'Admin pause via /admin/agents.',
    retrainingCadence: 'quarterly',
    frameworkRelevance: ['iso42001', 'finos_ai_v2'],
    iconColor: 'shield-check',
  },
];
// ─── Style Maps ─────────────────────────────────────────────────────────────

export const FRAMEWORK_STATUS_STYLES: Record<FrameworkStatus, { label: string; color: string }> = {
  not_started: { label: 'Not Started', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
  setup_in_progress: { label: 'Setup', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  audit_ready: { label: 'Audit Ready', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  certified: { label: 'Certified', color: 'bg-emerald-600/15 text-emerald-700 dark:text-emerald-300 border-emerald-600/30' },
  recertification_due: { label: 'Recert Due', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
};

export const CONTROL_STATUS_STYLES: Record<ControlStatus, { label: string; color: string }> = {
  passing: { label: 'Passing', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  failing: { label: 'Failing', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  partial: { label: 'Partial', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  needs_review: { label: 'Needs Review', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  not_applicable: { label: 'N/A', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
  not_implemented: { label: 'Not Implemented', color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20' },
};

export const RISK_RATING_STYLES: Record<'low' | 'medium' | 'high' | 'critical', { label: string; color: string }> = {
  low: { label: 'Low', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  medium: { label: 'Medium', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  high: { label: 'High', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20' },
  critical: { label: 'Critical', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
};

export const FRAMEWORK_CATEGORY_STYLES: Record<FrameworkCategory, { label: string; color: string }> = {
  security: { label: 'Security', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  privacy: { label: 'Privacy', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20' },
  export_control: { label: 'Export Control', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  healthcare: { label: 'Healthcare', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  ai_governance: { label: 'AI Governance', color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20' },
};
